/**
 * Subscription Service for Web
 * Handles subscription, billing, and payment operations
 */

import {httpsCallable} from 'firebase/functions';
import {functions} from '../config/firebaseInit';
import {getEnvironment} from '../config/firebase';

export type PlanType = 'individual' | 'starter' | 'professional' | 'enterprise';
export type SubscriptionStatus = 'trial' | 'active' | 'expired' | 'cancelled' | 'past_due';

export interface Subscription {
  organizationId: string;
  status: SubscriptionStatus;
  planType: PlanType;
  trialEndDate: string | null;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  memberLimit: number;
  environment: 'dev' | 'prod';
  createdAt: string;
  updatedAt: string;
}

export interface BillingContact {
  name: string;
  email: string;
  phone?: string;
  role?: string;
}

/**
 * Create subscription with trial
 */
export const createSubscription = async (
  organizationId: string,
  planType: PlanType,
  memberLimit?: number,
): Promise<Subscription> => {
  const fn = httpsCallable(functions, 'createSubscription');
  const result = await fn({
    organizationId,
    planType,
    memberLimit,
    environment: getEnvironment(),
  });

  return (result.data as any).subscription;
};

/**
 * Get subscription
 */
export const getSubscription = async (organizationId: string): Promise<Subscription | null> => {
  const fn = httpsCallable(functions, 'getSubscription');
  const result = await fn({organizationId});

  if (!(result.data as any).success) {
    return null;
  }

  return (result.data as any).subscription || null;
};

/**
 * Create Stripe checkout session
 * @param quantity - Number of users for per-user plans (defaults to 1)
 */
export const createCheckoutSession = async (
  organizationId: string,
  planType: PlanType,
  quantity?: number, // Number of users for per-user plans
  successUrl?: string,
  cancelUrl?: string,
): Promise<{sessionId: string; url: string}> => {
  const fn = httpsCallable(functions, 'createCheckoutSession');
  const result = await fn({
    organizationId,
    planType,
    quantity: quantity || 1,
    successUrl: successUrl || `${window.location.origin}/subscription/success`,
    cancelUrl: cancelUrl || `${window.location.origin}/subscription/cancel`,
  });

  return (result.data as any);
};

/**
 * Create billing portal session
 */
export const createPortalSession = async (
  organizationId: string,
  returnUrl: string,
): Promise<{url: string}> => {
  const fn = httpsCallable(functions, 'createPortalSession');
  const result = await fn({
    organizationId,
    returnUrl,
  });

  return (result.data as any);
};

/**
 * Set billing contact
 */
export const setBillingContact = async (
  organizationId: string,
  contact: BillingContact,
): Promise<void> => {
  const fn = httpsCallable(functions, 'setBillingContact');
  await fn({
    organizationId,
    contact,
  });
};

/**
 * Get billing contact
 */
export const getBillingContact = async (
  organizationId: string,
): Promise<BillingContact | null> => {
  const fn = httpsCallable(functions, 'getBillingContact');
  const result = await fn({organizationId});

  return (result.data as any).contact || null;
};

/**
 * Get invoices
 */
export const getInvoices = async (
  organizationId: string,
  limit?: number,
): Promise<any[]> => {
  const fn = httpsCallable(functions, 'getInvoices');
  const result = await fn({
    organizationId,
    limit,
  });

  return (result.data as any).invoices || [];
};

/**
 * Subscription audit event (history/log)
 */
export interface SubscriptionAuditEvent {
  eventId: string;
  organizationId: string;
  eventType: 'subscription_created' | 'subscription_status_changed' | 'subscription_plan_changed' | 'stripe_info_updated' | 'trial_expired' | 'payment_received' | 'payment_failed' | 'subscription_cancelled';
  timestamp: string;
  details: {
    previousStatus?: string;
    previousPlanType?: string;
    newStatus?: string;
    newPlanType?: string;
    triggeredBy?: 'user' | 'stripe_webhook' | 'scheduled_task' | 'admin' | 'system';
    userId?: string;
    stripeEventId?: string;
    stripeSubscriptionId?: string;
    stripeCustomerId?: string;
    reason?: string;
    metadata?: Record<string, any>;
  };
  environment: 'dev' | 'prod';
}

/**
 * Get subscription audit logs (history)
 */
export const getSubscriptionAuditLogs = async (
  organizationId: string,
  limit?: number,
): Promise<SubscriptionAuditEvent[]> => {
  const fn = httpsCallable(functions, 'getSubscriptionAuditLogs');
  const result = await fn({
    organizationId,
    limit,
  });

  return (result.data as any).events || [];
};

/**
 * Payment record interface
 */
export interface PaymentRecord {
  paymentId: string;
  organizationId: string;
  invoiceId?: string;
  stripeInvoiceId?: string;
  stripePaymentIntentId?: string;
  stripeChargeId?: string;
  amount: number;
  currency: string;
  status: 'succeeded' | 'pending' | 'failed' | 'refunded';
  paidAt: string;
  stripeReceiptUrl?: string;
  stripeReceiptNumber?: string;
  stripeHostedInvoiceUrl?: string;
  stripeInvoicePdf?: string;
  paymentMethod?: string;
  createdAt: string;
}

/**
 * Get payment history
 */
export const getPaymentHistory = async (
  organizationId: string,
  limit?: number,
): Promise<PaymentRecord[]> => {
  const fn = httpsCallable(functions, 'getPaymentHistory');
  const result = await fn({
    organizationId,
    limit,
  });

  return (result.data as any).payments || [];
};

